import { z } from 'zod';
import { Id, IsoDateTime } from './common.ts';

/**
 * Added by 21 step 0 (brief 21 §1, §1b.3; ADR-021): business deadlines. A deadline is metadata on the engine wait of
 * the activation that must finish by `at` (no separate scheduler): the orchestrator derives the wait timeout from it
 * through the `Calendar` port and, on timeout, emits `work-item.overdue` or `deadline.breached` and runs the item's
 * `Escalation` before the handler sees the timeout.
 *
 * - `sla`: a service-level target; a pack may extend it at runtime.
 * - `contractual`: agreed with a counterparty; extensions are recorded like SLA extensions.
 * - `statutory`: set by law or regulation; **never** extendable by a pack at runtime (`isDeadlineExtendable`).
 *
 * A hold pauses no deadline of any kind (v1): a deadline that breaches while held is recorded with `whileHeld`.
 */
export const DeadlineKind = z.enum(['sla', 'contractual', 'statutory']).meta({ id: 'DeadlineKind' });
export type DeadlineKind = z.infer<typeof DeadlineKind>;

/** Calendar id (`business-day` adapter: weekend days and holidays per id from configuration), e.g. `default`. */
export const CalendarId = z
  .string()
  .regex(/^[a-z][a-z0-9_.-]{0,62}$/, 'calendar id must be lowercase [a-z0-9_.-]')
  .meta({ id: 'CalendarId' });
export type CalendarId = z.infer<typeof CalendarId>;

export const Deadline = z
  .object({
    kind: DeadlineKind,
    /** Absolute due time (already computed in business time when `calendarId` is set). */
    at: IsoDateTime,
    /** What the deadline is based on (contract clause, regulation reference, SLA policy id); a pack value. */
    basisRef: z.string().min(1).max(500).optional(),
    /** Calendar the due time was computed with (business days); absent = calendar time. */
    calendarId: CalendarId.optional(),
  })
  .meta({ id: 'Deadline' });
export type Deadline = z.infer<typeof Deadline>;
export type DeadlineInput = z.input<typeof Deadline>;

/** True when a pack may move the deadline at runtime (`statutory` never). */
export function isDeadlineExtendable(kind: DeadlineKind): boolean {
  return kind !== 'statutory';
}

/** A deadline that passed: when, and whether the scope was held at the time (`breached_while_held`). */
export const DeadlineBreach = z
  .object({
    deadline: Deadline,
    at: IsoDateTime,
    /** Set when the breach happened during a hold: the hold and how long the scope had been held. */
    whileHeld: z.object({ holdId: Id, heldMs: z.number().int().nonnegative() }).optional(),
  })
  .meta({ id: 'DeadlineBreach' });
export type DeadlineBreach = z.infer<typeof DeadlineBreach>;

/** Timeline warning codes of deadlines (§1b.3). */
export const DeadlineWarningCode = z
  .enum(['deadline_at_risk', 'breached_while_held'])
  .meta({ id: 'DeadlineWarningCode' });
export type DeadlineWarningCode = z.infer<typeof DeadlineWarningCode>;

export const DEADLINE_EVENT_TYPES = { breached: 'deadline.breached' } as const;

/** `deadline.breached`: the deadline of an activation, approval request or obligation passed. */
export const DeadlineBreachedData = z
  .object({
    tenantId: z.string().min(1),
    caseId: Id.optional(),
    processInstanceId: Id.optional(),
    activationId: z.string().min(1).optional(),
    approvalRequestId: Id.optional(),
    obligationId: Id.optional(),
    breach: DeadlineBreach,
    commandId: z.string().min(1),
  })
  .meta({ id: 'DeadlineBreachedData' });
export type DeadlineBreachedData = z.infer<typeof DeadlineBreachedData>;
